import { redirect } from '@tanstack/react-router'
import { queryClient } from '@/app/queryClient'
import { meQueryKey, meQueryOptions } from '@/shared/api/meQueries'
import type { UserProfileData } from '@/shared/schemas/meApi.schema'

export type RequireAuthParams = {
  pathname: string
  search: unknown
}

const PUBLIC_PATHS = new Set(['/auth/login', '/auth/register', '/auth/forgot-password', '/auth/reset-password'])

/**
 * Préfixes accessibles sans authentification (CNIL/RGPD : mentions légales et
 * politique cookies doivent rester consultables hors connexion). Le shell
 * applicatif dégrade le header en lien « Connexion » si l'utilisateur n'est pas connecté.
 */
const PUBLIC_PREFIXES = ['/legal/']

function isPublicPath(pathname: string): boolean {
  return PUBLIC_PATHS.has(pathname) || PUBLIC_PREFIXES.some((prefix) => pathname.startsWith(prefix))
}

function normalizeSearch(search: unknown): string {
  if (typeof search === 'string') {
    return search.startsWith('?') || search === '' ? search : `?${search}`
  }

  if (search != null && typeof search === 'object') {
    const sp = new URLSearchParams(search as Record<string, string>)
    const s = sp.toString()
    return s === '' ? '' : `?${s}`
  }

  return ''
}

export async function requireAuth(params: RequireAuthParams): Promise<void> {
  const { pathname, search } = params

  if (isPublicPath(pathname)) {
    return
  }

  const from = `${pathname}${normalizeSearch(search)}`

  if (queryClient.getQueryData(meQueryKey) !== undefined) {
    return
  }

  try {
    await queryClient.fetchQuery(meQueryOptions)
    return
  } catch {
    // non connecté, session expirée ou erreur réseau → redirection login
  }

  try {
    sessionStorage.setItem('auth_redirect_reason', 'auth_required')
    sessionStorage.setItem('auth_redirect_from', from)
  } catch {
    // ignore storage failures
  }

  throw redirect({
    to: '/auth/login',
    search: { reason: 'auth_required' },
  })
}

/** Guard for `/auth/*` routes: bounce an already-authenticated user back to the portal home. */
export function redirectIfAuthenticated(): void {
  if (queryClient.getQueryData(meQueryKey) !== undefined) {
    throw redirect({ to: '/' })
  }
}

export async function requirePartnerRole(): Promise<void> {
  const cachedProfile = queryClient.getQueryData<UserProfileData>(meQueryKey)
  const profile = cachedProfile ?? (await queryClient.fetchQuery(meQueryOptions))

  if (!profile.roles.includes('partner')) {
    throw redirect({ to: '/' })
  }
}
